// (c) chaehongee. Confidential and proprietary.

import { getSupabaseClient } from './supabase'
import { telemetry } from './telemetry'

/**
 * AuthStateManager provides proactive token health checks and refresh logic.
 *
 * This prevents 401 errors by ensuring tokens are fresh before making requests,
 * and provides utilities for checking token validity.
 */
class AuthStateManager {
    /** Buffer time before token expiry to trigger proactive refresh (5 minutes) */
    private readonly tokenExpiryBuffer = 5 * 60 * 1000 // 5 minutes in ms

    /**
     * Check if the current token is valid and not close to expiring.
     *
     * @returns true if token exists and expires in more than 5 minutes
     */
    async isTokenValid(): Promise<boolean> {
        try {
            const supabase = getSupabaseClient()
            const {
                data: { session },
            } = await supabase.auth.getSession()

            if (!session) {
                return false
            }

            const expiresAt = session.expires_at ? session.expires_at * 1000 : 0
            const now = Date.now()

            // Token is valid if it exists and isn't close to expiring
            return expiresAt > now + this.tokenExpiryBuffer
        } catch (error) {
            console.error('Error checking token validity:', error)
            return false
        }
    }

    /**
     * Ensure we have a fresh token, proactively refreshing if needed.
     *
     * This method checks if the current token is close to expiring (within 5 minutes)
     * and refreshes it if necessary. This prevents 401 errors before they happen.
     *
     * @returns Fresh access token, or null if no session/refresh failed
     */
    async ensureFreshToken(): Promise<string | null> {
        try {
            const supabase = getSupabaseClient()
            const {
                data: { session },
            } = await supabase.auth.getSession()

            if (!session) {
                return null
            }

            const expiresAt = session.expires_at ? session.expires_at * 1000 : 0
            const now = Date.now()
            const isExpiringSoon = expiresAt <= now + this.tokenExpiryBuffer

            if (isExpiringSoon) {
                // Token is expiring soon - proactively refresh
                console.log('Token expiring soon, refreshing proactively')

                const { data, error } = await supabase.auth.refreshSession()

                if (error || !data.session) {
                    console.error('Failed to refresh token proactively:', error)
                    telemetry.trackEvent('auth_token_refresh', {
                        success: false,
                        reason: 'proactive',
                        error: error?.message,
                    })
                    return null
                }

                telemetry.trackEvent('auth_token_refresh', {
                    success: true,
                    reason: 'proactive',
                })

                return data.session.access_token
            }

            // Token is still valid, return it
            return session.access_token
        } catch (error) {
            console.error('Error ensuring fresh token:', error)
            return null
        }
    }

    /**
     * Get the current session's access token without refresh.
     *
     * @returns Current access token, or null if no session
     */
    async getCurrentToken(): Promise<string | null> {
        try {
            const supabase = getSupabaseClient()
            const {
                data: { session },
            } = await supabase.auth.getSession()

            return session?.access_token ?? null
        } catch (error) {
            console.error('Error getting current token:', error)
            return null
        }
    }
}

// Export singleton instance
export const authStateManager = new AuthStateManager()
